import { useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Edit, Trash2, User, RefreshCw, Eye, Paperclip } from "lucide-react";
import { Client } from "@/types/client";
import { useClientStatusOptions } from "@/hooks/useClientStatusOptions";
import { ClientProfilePopover } from "./ClientProfilePopover";
import { toast } from "sonner";
interface ClientsTableProps {
  clients: Client[];
  onEditClient: (client: Client) => void;
  onDeleteClient: (clientId: string) => void;
  onAddClient: () => void;
  onShowFiles?: (client: Client) => void;
}
export const ClientsTable = ({
  clients,
  onEditClient,
  onDeleteClient,
  onAddClient,
  onShowFiles
}: ClientsTableProps) => {
  const {
    getStatusStyle,
    getStatusLabel,
    isLoading: statusLoading,
    refetch: refetchStatuses
  } = useClientStatusOptions();

  // Force refresh statuses every 5 seconds to show updates
  useEffect(() => {
    const interval = setInterval(() => {
      console.log("Auto-refreshing status options...");
      refetchStatuses();
    }, 5000);
    return () => clearInterval(interval);
  }, [refetchStatuses]);
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-SA');
  };
  const handleManualRefresh = async () => {
    console.log("Manual refresh of statuses triggered...");
    toast.info("جاري تحديث الحالات...");
    try {
      await refetchStatuses();
      toast.success("تم تحديث الحالات بنجاح");
    } catch (error) {
      toast.error("حدث خطأ أثناء تحديث الحالات");
    }
  };
  if (clients.length === 0) {
    return <Card>
        <CardContent className="text-center py-12">
          <div className="text-gray-500 mb-4">لا توجد عملاء مطابقين للبحث</div>
          <Button onClick={onAddClient} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            إضافة عميل جديد
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card>
      
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
               <TableRow>
                 <TableHead className="text-right">الاسم بالعربية</TableHead>
                 <TableHead className="text-right">الشركة</TableHead>
                 <TableHead className="text-right">الجوال</TableHead>
                 <TableHead className="text-right">الحالة</TableHead>
                 <TableHead className="text-right">منشئ العميل</TableHead>
                 <TableHead className="text-right">الإجراءات</TableHead>
               </TableRow>
            </TableHeader>
             <TableBody>
               {clients.map(client => <TableRow key={client.id}>
                   <TableCell className="font-medium">
                     <span>{client.name_ar}</span>
                   </TableCell>
                   <TableCell>{client.company || 'غير محدد'}</TableCell>
                   <TableCell dir="ltr">{client.mobile || 'غير محدد'}</TableCell>
                   <TableCell>
                     <Badge className="text-white" style={getStatusStyle(client.status)}>
                       {getStatusLabel(client.status)}
                     </Badge>
                   </TableCell>
                   <TableCell>
                     <div className="flex items-center gap-2">
                       <User className="w-4 h-4 text-gray-500" />
                       <span className="text-sm">
                         {(client.creator && 'name' in client.creator ? client.creator.name : null) || client.employees?.name || 'غير محدد'}
                       </span>
                     </div>
                   </TableCell>
                   <TableCell>
                     <div className="flex items-center gap-1">
                       <ClientProfilePopover clientId={client.id}>
                         <Button variant="ghost" size="sm" className="h-8 px-2 text-xs">
                           <Eye className="w-3 h-3 mr-1" />
                           عرض
                         </Button>
                       </ClientProfilePopover>
                       
                       <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-blue-600 hover:text-blue-800" onClick={() => onEditClient(client)}>
                         <Edit className="w-3 h-3 mr-1" />
                         تعديل
                       </Button>
                       
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-green-600 hover:text-green-800" onClick={() => onShowFiles?.(client)}>
                          <Paperclip className="w-3 h-3 mr-1" />
                          الملفات ({client.attachments?.length || 0})
                        </Button>
                       
                       <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-red-600 hover:text-red-800" onClick={() => onDeleteClient(client.id)}>
                         <Trash2 className="w-3 h-3 mr-1" />
                         حذف
                       </Button>
                     </div>
                   </TableCell>
                 </TableRow>)}
             </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>;
};